export const normalizeRiskLevel = (value) => String(value || '').trim().toUpperCase();

export const isHighOrCriticalRisk = (value) => {
  const normalized = normalizeRiskLevel(value);
  return normalized === 'HIGH' || normalized === 'CRITICAL';
};

export const sortStrings = (values) => {
  if (!Array.isArray(values)) return [];
  return [...values].map((item) => String(item || '').trim()).filter(Boolean).sort((a, b) => a.localeCompare(b));
};

export const resolveApiError = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return fallback;
};

export const ROLE_PERMISSION_DOMAIN_GROUPS = [
  { key: 'tenants', label: 'Tenants', matchers: [/^platform\.tenants\./i] },
  { key: 'billing', label: 'Billing', matchers: [/^platform\.billing\./i] },
  { key: 'runtime', label: 'Runtime', matchers: [/^platform\.runtime\./i] },
  { key: 'recovery', label: 'Recovery', matchers: [/^platform\.recovery\./i] },
  { key: 'alerts', label: 'Alerts', matchers: [/^platform\.alerts\./i] },
  { key: 'catalog', label: 'Catalog', matchers: [/^platform\.catalog\./i] },
  { key: 'provider_config', label: 'Provider Config', matchers: [/^platform\.provider_config\./i] },
  { key: 'feature_flags', label: 'Feature Flags', matchers: [/^platform\.feature_flags\./i] },
  { key: 'iam', label: 'IAM', matchers: [/^platform\.iam\./i] },
  { key: 'readiness', label: 'Readiness', matchers: [/^platform\.readiness\./i] },
  { key: 'others', label: 'Outros', matchers: [] },
];

const toNormalizedString = (value) => String(value || '').trim();

const normalizeStringArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => toNormalizedString(item)).filter(Boolean);
};

const normalizeIdArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => toNormalizedString(item)).filter(Boolean);
};

const roleHasKey = (roleCandidate, roleKey) => toNormalizedString(roleCandidate).toLowerCase() === toNormalizedString(roleKey).toLowerCase();

const roleHasId = (roleCandidate, roleId) => toNormalizedString(roleCandidate) === toNormalizedString(roleId);

export const resolveRolePermissions = (role, permissions) => {
  const roleKey = toNormalizedString(role?.key);
  const roleId = toNormalizedString(role?.id);

  const permissionKeysFromRole = new Set([
    ...normalizeStringArray(role?.permissions),
    ...normalizeStringArray(role?.permission_keys),
  ]);
  const permissionIdsFromRole = new Set([
    ...normalizeIdArray(role?.permission_ids),
  ]);

  const normalizedPermissions = Array.isArray(permissions) ? permissions : [];

  return normalizedPermissions.filter((permission) => {
    const permissionKey = toNormalizedString(permission?.key);
    const permissionId = toNormalizedString(permission?.id);

    if (permissionKey && permissionKeysFromRole.has(permissionKey)) {
      return true;
    }

    if (permissionId && permissionIdsFromRole.has(permissionId)) {
      return true;
    }

    const permissionRoleKeys = [
      ...normalizeStringArray(permission?.roles),
      ...normalizeStringArray(permission?.role_keys),
    ];
    if (roleKey && permissionRoleKeys.some((item) => roleHasKey(item, roleKey))) {
      return true;
    }

    const permissionRoleIds = normalizeIdArray(permission?.role_ids);
    if (roleId && permissionRoleIds.some((item) => roleHasId(item, roleId))) {
      return true;
    }

    return false;
  });
};

export const getPermissionDomainLabel = (permissionKey) => {
  const normalized = toNormalizedString(permissionKey).toLowerCase();
  if (!normalized) return 'Outros';

  const match = ROLE_PERMISSION_DOMAIN_GROUPS.find((group) =>
    group.matchers.some((matcher) => matcher.test(normalized))
  );

  return (match || ROLE_PERMISSION_DOMAIN_GROUPS.find((group) => group.key === 'others')).label;
};

export const groupPermissionsByDomain = (permissions) => {
  const domainOrder = ROLE_PERMISSION_DOMAIN_GROUPS.map((group) => group.label);
  const buckets = new Map(domainOrder.map((label) => [label, []]));

  (Array.isArray(permissions) ? permissions : []).forEach((permission) => {
    const label = getPermissionDomainLabel(permission?.key);
    if (!buckets.has(label)) {
      buckets.set(label, []);
    }
    buckets.get(label).push(permission);
  });

  return domainOrder
    .map((label) => ({
      domain: label,
      items: sortStrings((buckets.get(label) || []).map((item) => item.key))
        .map((permissionKey) => (buckets.get(label) || []).find((item) => item?.key === permissionKey))
        .filter(Boolean),
    }))
    .filter((bucket) => bucket.items.length > 0);
};
